import React, { useContext } from 'react';
import AppContext from '../../contexts/AppContext';
import { User, Calendar, MessageCircle, Bell, Heart, Share2, Users, AtSign } from 'lucide-react';
import * as firestoreService from '../../services/firestoreService';
import { getRelativeTime } from '../../utils/formatting';
import Avatar from '../../components/common/Avatar';

const NotificationsView = () => {
  const { notifications, currentUser, users, viewUserProfile, setView } = useContext(AppContext);

  const handleMarkAllRead = async () => {
    try {
      await firestoreService.markAllNotificationsRead(currentUser.id);
    } catch (err) {
      console.error('Error marking all notifications read:', err);
    }
  };

  const handleNotifClick = async (notif) => {
    // Mark as read
    if (!notif.read) {
      try {
        await firestoreService.markNotificationRead(notif.id);
      } catch (err) {
        console.error('Error marking notification read:', err);
      }
    }
    // Navigate based on type
    if (notif.type === 'follow') {
      viewUserProfile(notif.fromUserId);
    } else if (notif.type === 'like' || notif.type === 'reaction' || notif.type === 'comment' || notif.type === 'comment_like' || notif.type === 'mention') {
      if (notif.postId) {
        setView('feed');
        const scrollToPost = () => {
          const el = document.getElementById(`post-${notif.postId}`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.classList.add('ring-2', 'ring-[#16a34a]');
            setTimeout(() => el.classList.remove('ring-2', 'ring-[#16a34a]'), 2000);
          }
        };
        setTimeout(scrollToPost, 500);
      }
    } else if (notif.type === 'share') {
      setView('messages');
    } else if (notif.type === 'eventInvite' && notif.eventId) {
      setView('events');
      setTimeout(() => {
        const el = document.getElementById(`event-${notif.eventId}`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.classList.add('ring-2', 'ring-[#16a34a]');
          setTimeout(() => el.classList.remove('ring-2', 'ring-[#16a34a]'), 2000);
        }
      }, 500);
    } else if (notif.type === 'event_join') {
      setView('events');
    } else if ((notif.type === 'group_invite' || notif.type === 'group_join_request') && notif.groupId) {
      setView(`group-detail-${notif.groupId}`);
    }
  };

  const getNotifIcon = (type) => {
    switch (type) {
      case 'like': case 'reaction': case 'comment_like': return <Heart className="w-5 h-5 text-red-500" />;
      case 'comment': return <MessageCircle className="w-5 h-5 text-blue-500" />;
      case 'mention': return <AtSign className="w-5 h-5 text-[#22c55e]" />;
      case 'follow': return <User className="w-5 h-5 text-[#16a34a]" />;
      case 'share': return <Share2 className="w-5 h-5 text-purple-500" />;
      case 'event_join': case 'eventInvite': return <Calendar className="w-5 h-5 text-yellow-500" />;
      case 'group_invite': return <Users className="w-5 h-5 text-[#16a34a]" />;
      case 'group_join_request': return <Users className="w-5 h-5 text-yellow-500" />;
      default: return <Bell className="w-5 h-5 text-gray-400" />;
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="max-w-2xl mx-auto p-4">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-white">Notifications</h2>
        {unreadCount > 0 && (
          <button onClick={handleMarkAllRead} className="text-sm text-[#16a34a] font-medium hover:text-[#15803d]">
            Mark all as read
          </button>
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="text-center py-12">
          <Bell className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-white mb-2">No notifications yet</h3>
          <p className="text-gray-400">When someone interacts with your posts or profile, you'll see it here.</p>
        </div>
      ) : (
        <div className="space-y-1">
          {notifications.map(notif => {
            const fromUser = users.find(u => u.id === notif.fromUserId);
            return (
              <button
                key={notif.id}
                onClick={() => handleNotifClick(notif)}
                className={`w-full flex items-start space-x-3 p-3 rounded-lg transition-colors text-left ${
                  notif.read ? 'hover:bg-gray-900' : 'bg-gray-900/50 hover:bg-gray-800'
                }`}
              >
                <div
                  className="flex-shrink-0 mt-0.5"
                  onClick={fromUser && notif.fromUserId !== 'system' ? (e) => { e.stopPropagation(); viewUserProfile(notif.fromUserId); } : undefined}
                >
                  {notif.fromUserId === 'system'
                    ? <div className="w-8 h-8 bg-[#16a34a] rounded-full flex items-center justify-center text-pure-white text-xs font-bold avatar-border">GC</div>
                    : fromUser ? <Avatar user={fromUser} size="sm" /> : getNotifIcon(notif.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm ${notif.read ? 'text-gray-400' : 'text-white'}`}>
                    {notif.fromUserId === 'system'
                      ? <span className="font-bold">Loft Team </span>
                      : fromUser && (
                        <span
                          className="font-bold hover:underline cursor-pointer"
                          onClick={(e) => { e.stopPropagation(); viewUserProfile(notif.fromUserId); }}
                        >
                          {fromUser.name}{' '}
                        </span>
                      )}
                    {notif.message}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">{getRelativeTime(notif.timestamp)}</p>
                </div>
                <div className="flex-shrink-0 mt-1">
                  {getNotifIcon(notif.type)}
                </div>
                {!notif.read && (
                  <div className="flex-shrink-0 mt-2">
                    <div className="w-2 h-2 bg-[#16a34a] rounded-full"></div>
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default NotificationsView;
